import apiClient from './apiClient';
import { User, DeletedUser, UserDeletionLog, UserRole } from '../types';

const apiBaseURL = '/users/';

// Interface pour les données de mise à jour du profil utilisateur
// Adaptez les champs selon ce que l'API /users/me (PUT/PATCH) accepte
export interface UserProfileUpdate {
  email?: string;
  name?: string; // Corrigé: Utiliser 'name' pour correspondre au type User et potentiellement à l'API
  // Ajoutez d'autres champs modifiables si nécessaire (ex: password, preferences...)
  // password?: string;
}

/**
 * Fetches the current logged-in user's data.
 * @returns A promise that resolves to the user data.
 */
export const fetchCurrentUser = async (): Promise<User> => {
  const response = await apiClient.get<User>(`${apiBaseURL}me`);
  console.log('[userService] /users/me response:', response.data);
  console.log('[userService] profile_id:', (response.data as any).profile_id, 'profile:', (response.data as any).profile);
  return response.data;
};

/**
 * Super-admin only: move the current user into another organization.
 * Returns the same shape as GET /users/me, with the new organization_id.
 */
export const switchOrganization = async (organizationId: string): Promise<User> => {
  const response = await apiClient.put<User>(`${apiBaseURL}me/organization`, {
    organization_id: organizationId,
  });
  return response.data;
};

/**
 * Updates the current logged-in user's profile data.
 * @param data The data to update the user profile with.
 * @returns A promise that resolves to the updated user data.
 */
export const updateUserProfile = async (data: UserProfileUpdate): Promise<User> => {
  // L'API backend utilise PUT pour /users/me
  const response = await apiClient.put<User>(`${apiBaseURL}me`, data);
  return response.data;
};

/**
 * Uploads the current user's avatar image.
 * Expects a File object (image/*); backend stores it in MinIO and returns updated User.
 */
export const uploadUserAvatar = async (file: File): Promise<User> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<User>(`${apiBaseURL}me/avatar`, formData);
  return response.data;
};

// Récupérer tous les utilisateurs (admin/super-admin). Optional limit for filter dropdowns.
export const fetchAllUsers = async (options?: { limit?: number }): Promise<User[]> => {
  const params = options?.limit != null ? { limit: options.limit } : undefined;
  const response = await apiClient.get<User[]>(apiBaseURL, { params });
  return response.data;
};

// Met à jour l'organisation d'un utilisateur (assignation / désassignation)
export const updateUserOrganization = async (
  userId: string,
  organizationId: string | null
): Promise<User> => {
  const response = await apiClient.put<User>(`${apiBaseURL}${userId}`, {
    organization_id: organizationId,
  });
  return response.data;
};

// Met à jour le rôle d'un utilisateur (Super Admin only can set any role)
export const updateUserRole = async (
  userId: string,
  role: UserRole
): Promise<User> => {
  const response = await apiClient.put<User>(`${apiBaseURL}${userId}`, { role });
  return response.data;
};

// Récupère un utilisateur par ID
export const fetchUserById = async (userId: string): Promise<User> => {
  const response = await apiClient.get<User>(`${apiBaseURL}${userId}`);
  return response.data;
};

// Delete a user (admin/super-admin only). When super-admin, also deletes from Logto if linked.
export const deleteUser = async (userId: string): Promise<User> => {
  const response = await apiClient.delete<User>(`${apiBaseURL}${userId}`);
  return response.data;
};

// Activate or deactivate a user (org-admin for own org members, super-admin for anyone)
export const toggleUserActive = async (userId: string, isActive: boolean): Promise<User> => {
  const response = await apiClient.put<User>(`${apiBaseURL}${userId}`, {
    is_active: isActive,
  });
  return response.data;
};

// Super-admin only: send password reset email via Logto (or return instructions if not available)
export const resendMagicLink = async (
  userId: string
): Promise<{ message: string; user_id: string; email: string; sent?: boolean }> => {
  const response = await apiClient.post<{
    message: string;
    user_id: string;
    email: string;
    sent?: boolean;
  }>(`${apiBaseURL}${userId}/resend-magic-link`);
  return response.data;
};

// Interface pour les préférences utilisateur
export interface UserPreferences {
  /** 'organization' follows the org's default theme; the rest map to next-themes. */
  theme?: 'organization' | 'light' | 'dark' | 'system';
  language?: 'fr' | 'en';
  notifications_enabled?: boolean;
  /** True once the user dismissed the first-run onboarding with "don't show again". */
  onboarding_completed?: boolean;
}

/**
 * Fetches the current user's preferences.
 * @returns A promise that resolves to the user preferences.
 */
export const fetchUserPreferences = async (): Promise<UserPreferences> => {
  const response = await apiClient.get<UserPreferences>(`${apiBaseURL}me/preferences`);
  return response.data;
};

/**
 * Updates the current user's preferences (merge).
 * @param preferences The preferences to update.
 * @returns A promise that resolves to the updated preferences.
 */
export const updateUserPreferences = async (preferences: UserPreferences): Promise<UserPreferences> => {
  const response = await apiClient.put<UserPreferences>(`${apiBaseURL}me/preferences`, preferences);
  return response.data;
};

/**
 * Permanently dismisses the first-run onboarding ("don't show again").
 * Persists the flag per-user so it follows the user across devices.
 */
export const markOnboardingCompleted = async (): Promise<UserPreferences> => {
  return updateUserPreferences({ onboarding_completed: true });
};

// Safe-delete a user and transfer their content to another user
export const safeDeleteUser = async (userId: string, transferToUserId: string): Promise<User> => {
  const response = await apiClient.post<User>(`${apiBaseURL}${userId}/safe-delete`, {
    transfer_to_user_id: transferToUserId,
  });
  return response.data;
};

// Restore a soft-deleted user (re-activates account, does NOT revert content ownership)
export const restoreUser = async (userId: string): Promise<User> => {
  const response = await apiClient.post<User>(`${apiBaseURL}${userId}/restore`);
  return response.data;
};

// Fetch all soft-deleted users (super-admin only)
export const getDeletedUsers = async (): Promise<DeletedUser[]> => {
  const response = await apiClient.get<DeletedUser[]>(`${apiBaseURL}deleted`);
  return response.data;
};

// Fetch the deletion log for a specific deleted user
export const getUserDeletionLog = async (userId: string): Promise<UserDeletionLog> => {
  const response = await apiClient.get<UserDeletionLog>(`${apiBaseURL}${userId}/deletion-log`);
  return response.data;
};

// Get users in the same org that can receive content from the user being deleted
export const getTransferCandidates = async (userId: string): Promise<User[]> => {
  const response = await apiClient.get<User[]>(`${apiBaseURL}${userId}/transfer-candidates`);
  return response.data;
};

const userService = {
  fetchCurrentUser,
  updateUserProfile,
  uploadUserAvatar,
  fetchAllUsers,
  updateUserOrganization,
  updateUserRole,
  getUserById: fetchUserById,
  deleteUser,
  toggleUserActive,
  resendMagicLink,
  fetchUserPreferences,
  updateUserPreferences,
  safeDeleteUser,
  restoreUser,
  getDeletedUsers,
  getUserDeletionLog,
  getTransferCandidates,
};

export default userService;
